import { AxiosError } from "axios";
import { useEffect, useState } from "react";
/**
 * useErrorBoundary hook
 *
 * This hook captures and handles errors that occur in the component tree, providing a way
 * to render a fallback UI in case of an unexpected crash or error.
 *
 * It sets up listeners for both `error` and `unhandledrejection` events, allowing it to
 * handle both synchronous and asynchronous errors. When an error is detected, the hook
 * updates the state to trigger a fallback UI or error message.
 *
 * @returns {ErrorBoundaryResult} - An object containing:
 *   - displayErrorMessage: A boolean indicating if the error message should be displayed.
 *   - hasError: A boolean indicating if an error has occurred.
 *   - error: The caught error object.
 *   - errorMessage: The message from the caught error.
 *   - resetError: A function to reset the error state (useful for retry actions).
 */

type AnyErr = unknown;

interface ErrorBoundaryResult {
  displayErrorMessage: boolean;
  hasError: boolean;
  error: AnyErr | null;
  errorMessage: string | null;
  resetError: () => void;
}

const WHITE_LISTED_ERRORS = [
  "ResizeObserver loop completed with undelivered notifications.",
];

export const useErrorBoundary = (): ErrorBoundaryResult => {
  const [displayErrorMessage, setDisplayErrorMessage] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<AnyErr>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Ignore certain errors/warnings that are not critical
      if (WHITE_LISTED_ERRORS.includes(event.message)) {
        return;
      }
      setDisplayErrorMessage(false);
      setHasError(true);
      setError(event.error ?? event); // keep original
      setErrorMessage(event?.message || "An unexpected error occurred.");
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      // Exclude Axios errors from being caught as unhandled rejections
      const r = event.reason;
      if (r?.isAxiosError || r instanceof AxiosError) return; // skip Axios
      setDisplayErrorMessage(false);
      setHasError(true);
      setError(r ?? event);
      setErrorMessage(r?.message || "An unhandled promise rejection occurred.");
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  const resetError = () => {
    setDisplayErrorMessage(false);
    setHasError(false);
    setError(null);
    setErrorMessage(null);
  };

  return { displayErrorMessage, hasError, error, errorMessage, resetError };
};
